import React from 'react';
import { Plus, ClipboardPaste } from 'lucide-react';
import { isSameDay } from '../../../../../utils/dateTime';

// Helper to format time consistently
const formatTime = (date: Date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

interface CopiedInterval {
  activityName: string;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  interval: {
    start: string;
  };
}

interface SlotContextMenuProps {
  x: number;
  y: number;
  start: Date;
  end: Date;
  gapStart: Date | null;
  gapEnd: Date | null;
  hasGap: boolean;
  prevEventEnd: Date | null;
  prevEventName: string | null;
  prevEventNearby: boolean;
  nextEventStart: Date | null;
  nextEventName: string | null;
  nextEventNearby: boolean;
  isClick: boolean;
  copiedInterval: CopiedInterval | null;
  onInsert: (start: Date, end: Date) => void;
  onPaste: (targetDate: Date) => void;
  pasteLabel: string;
}

const SlotContextMenu: React.FC<SlotContextMenuProps> = ({
  x,
  y,
  start,
  end,
  gapStart,
  gapEnd,
  hasGap,
  prevEventEnd,
  prevEventName,
  prevEventNearby,
  nextEventStart,
  nextEventName,
  nextEventNearby,
  isClick,
  copiedInterval,
  onInsert,
  onPaste,
  pasteLabel,
}) => {
  return (
    <div
      className="fixed z-50 min-w-[140px] rounded-md border bg-popover p-1 shadow-md"
      style={{ left: x, top: y }}
    >
      {/* Primary option - selected time range */}
      <button
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
        onClick={() => onInsert(start, end)}
      >
        <Plus className="h-4 w-4" />
        {`${formatTime(start)} - ${formatTime(end)}`}
      </button>

      {/* Alternative time options section */}
      {(hasGap || prevEventNearby || nextEventNearby) && (
        <div className="border-t border-border mt-1 pt-1">
          {/* Show "fill gap" option when both events are nearby */}
          {hasGap && gapStart && gapEnd && (
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              onClick={() => onInsert(gapStart, gapEnd)}
            >
              <Plus className="h-4 w-4" />
              {`"${prevEventName}" → "${nextEventName}"`}
            </button>
          )}
          {/* Show "from previous end" option when prev is nearby and it's not redundant with fill gap */}
          {prevEventNearby && prevEventEnd &&
           (!nextEventStart || end.getTime() !== nextEventStart.getTime()) && (
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              onClick={() => onInsert(prevEventEnd, end)}
            >
              <Plus className="h-4 w-4" />
              {`"${prevEventName}" → ${formatTime(end)}`}
            </button>
          )}
          {/* Show "to next event start" option when next is nearby and it's not redundant with fill gap */}
          {nextEventNearby && nextEventStart &&
           (!prevEventEnd || start.getTime() !== prevEventEnd.getTime()) && (
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              onClick={() => onInsert(start, nextEventStart)}
            >
              <Plus className="h-4 w-4" />
              {`${formatTime(start)} → "${nextEventName}"`}
            </button>
          )}
        </div>
      )}

      {/* Paste option - show only for clicks (not area selection) when there's a copied interval and not same day */}
      {isClick && copiedInterval && !isSameDay(start, new Date(copiedInterval.interval.start)) && (
        <button
          className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground border-t border-border mt-1 pt-1"
          onClick={() => onPaste(start)}
        >
          <ClipboardPaste className="h-4 w-4" />
          {`${pasteLabel} "${copiedInterval.activityName}" (${copiedInterval.startHour.toString().padStart(2, '0')}:${copiedInterval.startMinute.toString().padStart(2, '0')} - ${copiedInterval.endHour.toString().padStart(2, '0')}:${copiedInterval.endMinute.toString().padStart(2, '0')})`}
        </button>
      )}
    </div>
  );
};

export default SlotContextMenu;
